import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult } from "@tanstack/react-query";

interface DepartmentsTabProps {
  effectiveClientId: string | null;
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  createDepartmentMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteDepartmentMutation: UseMutationResult<unknown, unknown, string>;
  departmentsLoading: boolean;
  departmentsList: { id: string; name: string; is_active?: boolean }[];
}

export const DepartmentsTab: React.FC<DepartmentsTabProps> = ({
  effectiveClientId,
  managementState,
  onChange,
  createDepartmentMutation,
  updateDepartmentMutation,
  deleteDepartmentMutation,
  departmentsLoading,
  departmentsList,
}) => {
  const [edits, setEdits] = useState<Record<string, { name: string; is_active: boolean }>>({});

  useEffect(() => {
    if (!departmentsList.length) return;
    setEdits((prev) => {
      const next = { ...prev };
      departmentsList.forEach((department) => {
        if (!next[department.id]) {
          next[department.id] = {
            name: department.name,
            is_active: department.is_active !== false,
          };
        }
      });
      return next;
    });
  }, [departmentsList]);

  return (
  <SettingsSection
    title="Departments"
    description="Create departments and assign users."
  >
    {!effectiveClientId && (
      <Alert severity="info">Select a client to manage departments.</Alert>
    )}
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 5 }}>
        <SectionCard
          title="Create department"
          description="Add a new department for this client."
        >
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Department name"
              size="small"
              value={managementState.departmentName}
              onChange={(event) => onChange("departmentName", event.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={managementState.departmentActive}
                  onChange={(event) =>
                    onChange("departmentActive", event.target.checked)
                  }
                />
              }
              label="Active"
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                disabled={
                  !effectiveClientId ||
                  createDepartmentMutation.isPending ||
                  !managementState.departmentName.trim()
                }
                onClick={() =>
                  createDepartmentMutation.mutate({
                    name: managementState.departmentName.trim(),
                    is_active: managementState.departmentActive,
                  })
                }
              >
                Add department
              </Button>
            </Box>
          </Stack>
        </SectionCard>
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        <SectionCard
          title="Existing departments"
          description="Departments available for assignment."
        >
          {departmentsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading departments...</Typography>
            </Stack>
          ) : departmentsList.length ? (
            <TableContainer sx={{ width: "100%" }}>
              <Table size="small" sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentsList.map((department) => {
                    const edit = edits[department.id] ?? {
                      name: department.name,
                      is_active: department.is_active !== false,
                    };
                    const hasChanges =
                      edit.name !== department.name ||
                      edit.is_active !== (department.is_active !== false);
                    return (
                      <TableRow key={department.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            label="Department name"
                            value={edit.name}
                            onChange={(event) =>
                              setEdits((prev) => ({
                                ...prev,
                                [department.id]: {
                                  ...edit,
                                  name: event.target.value,
                                },
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={edit.is_active}
                                onChange={(event) =>
                                  setEdits((prev) => ({
                                    ...prev,
                                    [department.id]: {
                                      ...edit,
                                      is_active: event.target.checked,
                                    },
                                  }))
                                }
                              />
                            }
                            label={edit.is_active ? "Active" : "Inactive"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={
                                !hasChanges || updateDepartmentMutation.isPending
                              }
                              onClick={() =>
                                updateDepartmentMutation.mutate({
                                  departmentId: department.id,
                                  payload: {
                                    name: edit.name,
                                    is_active: edit.is_active,
                                  },
                                })
                              }
                            >
                              Save
                            </Button>
                            <Button
                              color="error"
                              variant="text"
                              size="small"
                              disabled={deleteDepartmentMutation.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Delete this department? This cannot be undone."
                                  )
                                ) {
                                  deleteDepartmentMutation.mutate(department.id);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No departments found yet.
            </Typography>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  </SettingsSection>
  );
};
